import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import { url, filename } from "aldoo-ra/CMS/url"
import ImagePreview from "aldoo-ra/CMS/preview/image-preview"
import VideoPreview from "aldoo-ra/CMS/preview/video-preview"
import VideoThumbnail from "aldoo-ra/CMS/preview/video-thumbnail"

// Define file type arrays
const imageTypes = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"]
const videoTypes = [".mp4", ".webm", ".mov", ".avi"]
const audioTypes = [".mp3", ".ogg", ".wav", ".aac"]

// Helper function to get friendly file name without timestamp hashes or extension
const getFriendlyFileName = (filename) => {
  if (!filename) return "Unnamed"

  // Extract just the filename if path is given
  const baseName = filename.split("/").pop() || filename

  // Split extension and name
  const lastDotIndex = baseName.lastIndexOf(".")
  const nameWithoutExtension =
    lastDotIndex !== -1 ? baseName.substring(0, lastDotIndex) : baseName

  // Pattern: matches _numbers_alphanumeric at the end of string before extension
  // Example: name_1735819725029_2a0cc3ec8d07315a.ext -> name
  const timestampHashPattern = /_\d+_[a-zA-Z0-9]+$/

  // Remove the timestamp and hash part
  const friendlyName = nameWithoutExtension.replace(timestampHashPattern, "")

  return friendlyName
}

export default function ThumbnailView({ file, onClick, onKeyDown, isSelected }) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // Extract file information from path
  const getFileExtension = () => {
    if (file.extension) return file.extension
    if (file.path) {
      const parts = file.path.split(".")
      return parts.length > 1 ? parts.pop().toLowerCase() : ""
    }
    return ""
  }

  const getFileName = () => {
    if (file.name) return file.name
    if (file.path) {
      const pathParts = file.path.split("/")
      return pathParts.pop() || "Unnamed"
    }
    return "Unnamed"
  }

  // Get extension early so we can use it throughout the component
  const extension = getFileExtension()
  const originalFileName = getFileName()

  // Get the friendly file name (without timestamp hashes)
  const displayFileName = getFriendlyFileName(originalFileName)

  // Get file URL, ensuring it's properly formatted
  const getFileUrl = () => {
    if (file.url) return file.url
    if (file.path) return url(file.path)
    return null
  }

  // Determine file types
  const isImageFile = extension
    ? imageTypes.includes(`.${extension.toLowerCase()}`)
    : false
  const isVideoFile = extension
    ? videoTypes.includes(`.${extension.toLowerCase()}`)
    : false
  const isAudioFile = extension
    ? audioTypes.includes(`.${extension.toLowerCase()}`)
    : false

  // Determine if file has a preview capability
  const hasPreview = isImageFile || isVideoFile

  // Determine file icon based on extension
  const getFileIcon = () => {
    if (!extension) return "?"

    switch (extension.toLowerCase()) {
      case "pdf":
        return "PDF"
      case "doc":
      case "docx":
        return "DOC"
      case "xls":
      case "xlsx":
        return "XLS"
      case "ppt":
      case "pptx":
        return "PPT"
      case "txt":
        return "TXT"
      case "zip":
      case "rar":
      case "7z":
        return "ZIP"
      default:
        return extension.toUpperCase().substring(0, 3)
    }
  }

  const handleThumbnailClick = (e) => {
    // Stop propagation to prevent the modal from closing
    e.stopPropagation()

    // Only call the parent onClick handler for selection
    // We don't auto-open the preview anymore
    if (onClick) {
      onClick(e, file)
    }
  }

  const handleThumbnailKeyDown = (e) => {
    // Stop propagation to prevent bubbling
    e.stopPropagation()

    if (onKeyDown) {
      onKeyDown(e, file)
    }
  }

  return (
    <>
      <div
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        className={`relative overflow-hidden rounded-md cursor-pointer transition-all duration-200 transform
                  ${
                    isSelected
                      ? "ring-2 ring-admin_primary shadow-lg scale-[1.02] z-10"
                      : "hover:shadow-lg hover:scale-[1.02] hover:z-10"
                  }`}
        onClick={handleThumbnailClick}
        onKeyDown={handleThumbnailKeyDown}
        tabIndex={0}
        role="button"
        title={originalFileName} // Show original filename on hover
      >
        {/* Main content area */}
        <div className="w-full h-24 bg-admin_paper">
          {isImageFile && getFileUrl() ? (
            <img
              src={getFileUrl()}
              alt={displayFileName}
              className="w-full h-full object-contain"
              loading="lazy"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
            />
          ) : isVideoFile && getFileUrl() ? (
            <VideoThumbnail
              videoUrl={getFileUrl()}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-admin_canvas">
              <div className="w-16 h-16 flex items-center justify-center bg-admin_paper rounded-full">
                <Typography className="text-xl font-bold text-admin_text_secondary">
                  {getFileIcon()}
                </Typography>
              </div>
            </div>
          )}
        </div>

      </div>
    </>
  )
}
